import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, FileArchive, Sparkles, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

const STATUS_LABEL = {
    uploaded: "Uploaded",
    extracting: "Extracting files…",
    parsing_specs: "Parsing spec book…",
    ready: "Ready",
    converted: "Converted",
    failed: "Failed",
};

/** Bid documents card on the Lead detail page.
 * Upload a zip → pipeline runs → generate pre-estimate → lead auto-advances. */
export default function LeadBidIntakeCard({ leadId, jobIds = [], onEstimateCreated }) {
    const [jobs, setJobs] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [busyJobId, setBusyJobId] = useState(null);
    const [err, setErr] = useState("");
    const fileRef = useRef(null);
    const pollRef = useRef(null);

    const loadJobs = async () => {
        if (jobIds.length === 0) {
            setJobs([]);
            return;
        }
        try {
            const results = await Promise.all(
                jobIds.map((id) =>
                    api.get(`/bid-intake/jobs/${id}`).then((r) => r.data).catch(() => null),
                ),
            );
            const clean = results.filter(Boolean);
            setJobs(clean);
            const pending = clean.some((j) =>
                ["uploaded", "extracting", "parsing_specs"].includes(j.status),
            );
            if (pending) pollRef.current = setTimeout(loadJobs, 2500);
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => {
        loadJobs();
        return () => pollRef.current && clearTimeout(pollRef.current);
        // eslint-disable-next-line
    }, [JSON.stringify(jobIds)]);

    const upload = async (file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".zip")) {
            setErr("Only .zip uploads are supported.");
            return;
        }
        setErr("");
        setUploading(true);
        setProgress(0);
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("lead_id", leadId);
            await api.post("/bid-intake/upload", form, {
                onUploadProgress: (e) => {
                    if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
                },
            });
            toast.success("Zip uploaded — parsing spec book…", { duration: 3000 });
            // Give the parent a beat to refresh the lead (which will pull the new job id)
            setTimeout(() => onEstimateCreated?.(), 600);
        } catch (e) {
            setErr(apiErrorMessage(e, "Upload failed"));
        } finally {
            setUploading(false);
            setProgress(0);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const generate = async (job) => {
        setBusyJobId(job.id);
        setErr("");
        try {
            const { data } = await api.post(`/bid-intake/jobs/${job.id}/pre-estimate`, {});
            toast.success(`Pre-estimate ${data.estimate_number} created`, {
                duration: 4000,
                action: {
                    label: "Open",
                    onClick: () => (window.location.href = `/estimates/${data.estimate_id}`),
                },
            });
            // Optimistically flip this job so the button becomes "Open estimate"
            // without waiting for a page reload.
            setJobs((prev) =>
                prev.map((j) =>
                    j.id === job.id
                        ? { ...j, pre_estimate_id: data.estimate_id, status: "converted" }
                        : j,
                ),
            );
            onEstimateCreated?.(data.estimate_id);
        } catch (e) {
            setErr(apiErrorMessage(e, "Could not generate pre-estimate"));
        } finally {
            setBusyJobId(null);
        }
    };

    return (
        <div className="brand-card fade-up" data-testid="lead-bid-intake-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="eyebrow">Bid documents</p>
                    <h3 className="h3 mt-1">AI-assisted estimate</h3>
                    <p className="text-muted text-sm mt-1">
                        Upload the bid zip — spec book parsing runs automatically, then generate a
                        pre-estimate pre-filled with this lead's contact info.
                    </p>
                </div>
                <div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".zip,application/zip"
                        onChange={(e) => upload(e.target.files?.[0])}
                        className="hidden"
                        data-testid="lead-bid-upload-input"
                    />
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={uploading}
                        onClick={() => fileRef.current?.click()}
                        data-testid="lead-bid-upload-btn"
                    >
                        <Upload size={14} />
                        {uploading ? `Uploading… ${progress}%` : "Upload zip"}
                    </button>
                </div>
            </div>

            {err && (
                <p
                    className="text-sm mt-3 flex items-center gap-1"
                    style={{ color: "var(--brand-red)" }}
                    data-testid="lead-bid-error"
                >
                    <AlertTriangle size={13} /> {err}
                </p>
            )}

            {jobs.length === 0 ? (
                <div className="mt-4 flex items-center gap-2 text-muted text-sm">
                    <FileArchive size={14} />
                    No bid documents uploaded yet.
                </div>
            ) : (
                <ul className="mt-4 flex flex-col gap-2" data-testid="lead-bid-jobs">
                    {jobs.map((j) => {
                        const paintCount = j.extracted_data?.paint_systems?.length || 0;
                        const isFailed = j.status === "failed";
                        return (
                            <li
                                key={j.id}
                                className="rounded-md p-3 flex items-start justify-between gap-3 flex-wrap"
                                style={{
                                    background: "rgba(0,0,0,0.03)",
                                    borderLeft: `3px solid ${
                                        isFailed
                                            ? "var(--brand-red)"
                                            : j.status === "ready"
                                                ? "var(--brand-success)"
                                                : "var(--brand-warning)"
                                    }`,
                                }}
                                data-testid={`lead-bid-job-${j.id}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <p className="font-semibold text-sm truncate">
                                        {j.original_filename}
                                    </p>
                                    <p className="text-xs text-muted mt-0.5">
                                        {STATUS_LABEL[j.status] || j.status}
                                        {" · "}
                                        {j.file_count || 0} files
                                        {paintCount > 0 && (
                                            <>
                                                {" · "}
                                                <span
                                                    style={{ color: "var(--brand-success)" }}
                                                >
                                                    <CheckCircle2
                                                        size={11}
                                                        className="inline mr-0.5"
                                                    />
                                                    {paintCount} paint system
                                                    {paintCount === 1 ? "" : "s"}
                                                </span>
                                            </>
                                        )}
                                        {" · "}
                                        {fmtDateTime(j.created_at)}
                                    </p>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <Link
                                        to={`/admin/bid-intake/${j.id}`}
                                        className="btn btn-ghost text-xs"
                                        data-testid={`lead-bid-job-detail-${j.id}`}
                                    >
                                        Details
                                    </Link>
                                    {j.pre_estimate_id ? (
                                        <Link
                                            to={`/estimates/${j.pre_estimate_id}`}
                                            className="btn btn-secondary text-xs"
                                            data-testid={`lead-bid-open-estimate-${j.id}`}
                                        >
                                            Open estimate
                                        </Link>
                                    ) : j.status === "ready" ? (
                                        <button
                                            type="button"
                                            className="btn btn-primary text-xs"
                                            disabled={busyJobId === j.id}
                                            onClick={() => generate(j)}
                                            data-testid={`lead-bid-generate-${j.id}`}
                                        >
                                            <Sparkles size={12} />
                                            {busyJobId === j.id
                                                ? "Generating…"
                                                : "Generate pre-estimate"}
                                        </button>
                                    ) : null}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
